import React, { useEffect, useState, useRef } from 'react';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';

// Extend Document interface for View Transitions API
interface DocumentWithViewTransition extends Document {
  startViewTransition?: (callback: () => void | Promise<void>) => {
    ready: Promise<void>;
    finished: Promise<void>;
    updateCallbackDone: Promise<void>;
  };
}

export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const toggleTheme = async () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    const doc = document as DocumentWithViewTransition;

    // Check if View Transitions API is supported
    if (!doc.startViewTransition || !buttonRef.current) {
      setTheme(newTheme);
      return;
    }

    // Add transitioning class for optimization
    document.documentElement.classList.add('theme-transitioning');

    // Get button position for circle animation origin
    const rect = buttonRef.current.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;

    // Calculate the radius needed to cover the entire viewport (with some buffer)
    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    ) * 1.5;

    // Start the view transition with circle-blur animation
    const transition = doc.startViewTransition(() => {
      setTheme(newTheme);
    });

    try {
      await transition.ready;

      // Animate the circle from button center
      document.documentElement.animate(
        {
          clipPath: [
            `circle(0px at ${x}px ${y}px)`,
            `circle(${endRadius}px at ${x}px ${y}px)`,
          ],
        },
        {
          duration: 800,
          pseudoElement: '::view-transition-new(root)',
        }
      );

      await transition.finished;
    } finally {
      // Remove transitioning class
      document.documentElement.classList.remove('theme-transitioning');
    }
  };

  if (!mounted) {
    return (
      <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 animate-pulse" />
    );
  }

  return (
    <button
      ref={buttonRef}
      onClick={toggleTheme}
      className="relative p-2 flex items-center justify-center hover:opacity-70 transition-all duration-200"
      aria-label="Toggle theme"
    >
      {theme === 'dark' ? (
        <Moon className="h-4 w-4 text-gray-400 dark:text-gray-400 transition-all duration-300" />
      ) : (
        <Sun className="h-4 w-4 text-gray-600 dark:text-gray-400 transition-all duration-300" />
      )}
    </button>
  );
}
